import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { KeyRound, LinkIcon } from 'lucide-react';
import './styles/LoginPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REASONS = {
  used: 'This reset link has already been used.',
  expired: 'This reset link has expired — links only work for 1 hour.',
  invalid: 'This reset link is not valid.',
};

export default function ResetPasswordPage({ setUser }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ checking: true, valid: false, reason: 'invalid', email: '', firstTime: false });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/reset-password/validate`, { params: { token } })
      .then(({ data }) => { if (!cancelled) setState({ checking: false, valid: data.valid, reason: data.reason || 'invalid', email: data.email || '', firstTime: Boolean(data.first_time) }); })
      .catch(() => { if (!cancelled) setState({ checking: false, valid: false, reason: 'invalid', email: '', firstTime: false }); });
    return () => { cancelled = true; };
  }, [token]);

  const validate = () => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (state.email && password.trim().toLowerCase() === state.email.trim().toLowerCase()) return 'Password cannot be your email address';
    if (password !== confirm) return 'Passwords do not match';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, password, confirm_password: confirm });
      localStorage.setItem('session_token', data.session_token);
      setUser(data.user);
      toast.success(state.firstTime ? 'Password created — you are signed in' : 'Password updated — other devices have been signed out');
      navigate('/dashboard/kanban', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not reset the password';
      if (/used|expired|not valid/i.test(detail)) {
        setState((s) => ({ ...s, valid: false, reason: /used/i.test(detail) ? 'used' : /expired/i.test(detail) ? 'expired' : 'invalid' }));
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page" data-testid="reset-password-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/vibely-logo-light.svg" alt="Vibely" className="login-logo" />
          <p className="tagline">{state.firstTime ? 'Create your password' : 'Choose a new password'}</p>
        </div>

        <Card className="login-card">
          {state.checking ? (
            <p className="reset-hint" data-testid="reset-checking">Checking your link…</p>
          ) : !state.valid ? (
            <div className="reset-confirmation" data-testid="reset-invalid">
              <LinkIcon size={32} aria-hidden="true" />
              <h2>Link not usable</h2>
              <p data-testid="reset-invalid-reason">{REASONS[state.reason] || REASONS.invalid}</p>
              <Button type="button" className="submit-button" onClick={() => navigate('/login?forgot=1')} data-testid="reset-request-new">
                Request a new link
              </Button>
              <button type="button" className="toggle-button" onClick={() => navigate('/login')} data-testid="reset-back-login">Back to sign in</button>
            </div>
          ) : (
            <>
              <div className="card-header">
                <h2>{state.firstTime ? 'Set your password' : 'Reset your password'}</h2>
                <p>
                  {state.email ? <>for <strong data-testid="reset-email">{state.email}</strong>. </> : null}
                  {state.firstTime ? 'You can keep signing in with Google too.' : 'Every other signed-in device will be signed out.'}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="login-form" noValidate>
                <div className="form-group">
                  <label htmlFor="new-password">New password</label>
                  <Input id="new-password" type="password" autoComplete="new-password" placeholder="At least 8 characters" value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }} required autoFocus data-testid="reset-new-password" />
                </div>
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm password</label>
                  <Input id="confirm-password" type="password" autoComplete="new-password" placeholder="Repeat the password" value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setError(''); }} required data-testid="reset-confirm-password" />
                </div>
                <p className="reset-hint">At least 8 characters and different from your email address.</p>
                {error && <p className="form-error" role="alert" data-testid="reset-error">{error}</p>}
                <Button type="submit" className="submit-button" disabled={loading || !password || !confirm} data-testid="reset-submit">
                  <KeyRound size={18} /> <span>{loading ? 'Saving…' : state.firstTime ? 'Create password and sign in' : 'Reset password and sign in'}</span>
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
